import React, { useState, useEffect, useRef, useMemo, forwardRef } from 'react';
import { Redirect } from 'react-router-dom';
import MaterialTable from 'material-table';
import { MuiThemeProvider } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Loader from '../../../Loader.js';
import Rodal from 'rodal';
import 'rodal/lib/rodal.css';
import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import { LocalizationTheme } from '../../../../utils/utils.js';
import { DisplayMessage } from '../../../../services/helpers.js';
import FontDownloadRoundedIcon from '@material-ui/icons/FontDownloadRounded';
import { CircularProgress, FormControl, InputLabel, Paper, Select } from '@material-ui/core';
import moment from 'moment';
import { sortTableDates, sortTableStringsNotCaseSensitive } from '../../../../utils/tableSorting.js';
import appFetch from '../../../../api/appFetch.js';
import { LinkedWorkshopAgendasWithIdUrl, LinkedWorkshopAgendasWithOutletIdUrl, OutletUsersNotDeletedUrl, WorkshopAgendasUrl } from '../../../../constants/apiUrls.js';
import OutletsDropdown from '../../../Common/Dropdowns/OutletsDropdown.js';
import { withPlanningSettings } from '../../../../utils/withPlanningSettings.js';

const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
    FontDownloadRoundedIcon: forwardRef((props, ref) => <FontDownloadRoundedIcon {...props} ref={ref} />)
};

const LinkAgendasTable = (props) => {
    const { resources, accessibleMoboOutlets, refreshGrid, onOutletChangeForSettings, canUseAgendasAndAbsencesInSettings } = props;

    const [outlets] = useState(() => {
        if (accessibleMoboOutlets !== undefined) {
            return [...accessibleMoboOutlets].sort((a, b) => a.description.toLowerCase().localeCompare(b.description.toLowerCase()));
        }
        return [];
    });

    const [outletId, setOutletId] = useState(() => accessibleMoboOutlets !== undefined ? accessibleMoboOutlets[0].id : 0);
    const [dataGrid, setDataGrid] = useState([]);
    const [fetchingData, setFetchingData] = useState(true);
    const [showDelete, setShowDelete] = useState(false);
    const [deletedItem, setDeletedItem] = useState(0);
    const [editedLinkAgendaId, setEditedLinkAgendaId] = useState(undefined);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [redirect, setRedirect] = useState(null);
    const [linkAgendasItems, setLinkAgendasItems] = useState([]);

    const outletIdRef = useRef(outletId);
    useEffect(() => { outletIdRef.current = outletId; }, [outletId]);

    const canEdit = canUseAgendasAndAbsencesInSettings !== false;

    const getData = (outletId) => {
        setFetchingData(true);
        let initialData = {};
        initialData.columns = [
            {
                title: resources["OutletUser"],
                field: 'outletUserName',
                options: { filter: true, sort: true },
                customSort: (a, b) => sortTableStringsNotCaseSensitive(a.outletUserName, b.outletUserName)
            },
            {
                title: resources["StartOn"],
                field: 'activeFrom',
                options: { filter: true, sort: true },
                customSort: (a, b) => sortTableDates(a.activeFrom, b.activeFrom)
            },
            {
                title: resources["EndsOn"],
                field: 'activeTill',
                options: { filter: true, sort: true },
                customSort: (a, b) => sortTableDates(a.activeTill, b.activeTill)
            },
        ];

        appFetch({
            url: LinkedWorkshopAgendasWithOutletIdUrl(outletId),
            onSuccess: (data) => {
                const currentRows = [];
                var agendaItems = [];

                data.sort((el1, el2) => el1.id - el2.id).forEach(function (rowData) {
                    agendaItems.push(rowData);

                    currentRows.push({
                        id: rowData.id,
                        outletUserName: rowData.outletUser !== null ? rowData.outletUser.firstName + " " + rowData.outletUser.lastName : "",
                        activeFrom: moment(rowData.activeFrom).format('DD/MM/YYYY'),
                        activeTill: rowData.activeTill !== null ? moment(rowData.activeTill).format('DD/MM/YYYY') : "",
                    });
                });

                initialData.data = currentRows;

                setDataGrid(initialData);
                setLinkAgendasItems(agendaItems);
            },
            onError: (data) => {
                for (var i = 0; i < data.errors.length; i++) {
                    DisplayMessage(data.errors[i], "error");
                }

                setDataGrid([]);
            },
            onFinalize: () => {
                setFetchingData(false);
            }
        })
    };

    useEffect(() => {
        if (onOutletChangeForSettings && outletIdRef.current) {
            onOutletChangeForSettings(outletIdRef.current);
        }

        const id = outletIdRef.current;
        if (id > 0) {
            getData(id);
        } else if (outlets[0] !== undefined) {
            getData(outlets[0].id);
        }
    }, []);

    useEffect(() => {
        if (refreshGrid) {
            getData(outletIdRef.current);
        }
        setRedirect(null);
    }, [refreshGrid]);

    const handleChangeOutlet = (e) => {
        const id = e.target.value;
        setOutletId(id);
        getData(id);
        if (onOutletChangeForSettings) {
            onOutletChangeForSettings(id);
        }
    };

    const handleCloseDelete = () => {
        setShowDelete(false);
        setDeletedItem(undefined);
    };

    const handleDeleteAgenda = () => {
        setRequestInProgress(true);
        const url = LinkedWorkshopAgendasWithIdUrl(deletedItem);

        appFetch({
            url: url,
            method: 'DELETE',
            onSuccess: () => {
                DisplayMessage(resources["LinkAgendaWasDeleted"], "success");
                getData(outletIdRef.current);
            },
            onError: (data) => {
                for (var i = 0; i < data.errors.length; i++) {
                    DisplayMessage(data.errors[i], "error");
                }
            },
            onFinalize: () => {
                setShowDelete(false);
                setDeletedItem(0);
                setRequestInProgress(false);
            }
        })
    };

    const handleAddNewLinkAgenda = () => {
        setRedirect(`linkagendas/new`);
        setEditedLinkAgendaId(undefined);
    };

    const tableActions = useMemo(() => [
        {
            icon: () => <Edit style={{ opacity: canEdit ? 1 : 0.5 }} />,
            tooltip: resources["Edit"],
            disabled: !canEdit,
            onClick: (event, rowData) => {
                if (!canEdit) return;
                setRedirect(`linkagendas/${outletIdRef.current}/${rowData.id}`);
                setEditedLinkAgendaId(rowData.id);
            }
        },
        {
            icon: () => <DeleteOutline style={{ opacity: canEdit ? 1 : 0.5 }} />,
            tooltip: resources["Delete"],
            disabled: !canEdit,
            onClick: (event, rowData) => {
                if (!canEdit) return;
                setShowDelete(true);
                setDeletedItem(rowData.id);
            }
        }
    ], [canEdit, resources]);

    if (redirect) {
        return <Redirect to={{
            pathname: redirect,
            state: { editedLinkAgendaId: editedLinkAgendaId, outletId: outletId, linkAgendasItems: linkAgendasItems, refreshGrid: true }
        }} />
    }

    return (
        <div>
            <div className="position-relative py-4">
                <h3>{resources["LinkAgendas"]}</h3>
                <div className={"offcentered-loader " + (fetchingData ? 'fadeIn' : 'fadeOut')}><CircularProgress /></div>

                <div className={(fetchingData ? 'fadeOut' : 'fadeIn')}>

                    <div className="float-left mt-3 col-md-4 pl-0" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <OutletsDropdown
                            resources={resources}
                            outlets={accessibleMoboOutlets}
                            outlet={outletId}
                            onOutletChange={handleChangeOutlet}
                            id="outlet-dropdown-grid"
                            containerClass="col-md-12"
                            noPlaceholder
                        />
                        {!canEdit && (
                            <Typography variant="body2" color="error" style={{ whiteSpace: 'nowrap' }}>
                                {resources["UseShiftsFunctionalityToManageAgendasOrAbsences"]}
                            </Typography>
                        )}
                    </div>
                    <div className="float-right mt-3">
                        <div className="float-right mb-3" >
                            <Button id="addNew" onClick={handleAddNewLinkAgenda} color="primary" variant="contained" disabled={!canEdit}>
                                <i className="fas fa-plus mr-2" title={resources["AddNewLinkAgendatitle"]} />
                                {resources["AddNewLinkAgenda"]}
                            </Button>
                        </div>
                    </div>
                    <div style={{ clear: "both", }}></div>

                    <Paper elevation={3} className="p-3 mt-3">
                        <MuiThemeProvider theme={LocalizationTheme()}>
                            <MaterialTable
                                icons={tableIcons}
                                title={""}
                                localization={{
                                    header: { actions: resources["Actions"] },
                                    body: {
                                        emptyDataSourceMessage: resources["NoRecordsToBeDisplayed"],
                                        deleteTooltip: resources["DeleteTooltip"],
                                        editTooltip: resources["EditTooltip"],
                                    },
                                    toolbar: {
                                        searchPlaceholder: resources["Search"],
                                        searchTooltip: resources["Search"]
                                    },
                                    pagination: {
                                        firstTooltip: resources["FirstTooltip"],
                                        lastTooltip: resources["LastTooltip"],

                                        previousTooltip: resources["PreviousTooltip"],
                                        nextTooltip: resources["NextTooltip"],

                                        labelRowsPerPage: resources["LabelRowsPerPage"],
                                        labelRowsSelect: resources["LabelRowsSelect"],
                                    }
                                }}

                                data={dataGrid.data}
                                columns={dataGrid.columns}
                                actions={tableActions}
                                options={{ emptyRowsWhenPaging: false, pageSize: 10, pageSizeOptions: [10, 15, 20], draggable: true }}
                            />
                        </MuiThemeProvider>
                    </Paper>
                </div>
                <div className={"loader-overlap position-absolute " + (fetchingData ? 'fadeIn' : 'fadeOut')}><div className="container"><Loader /></div></div>

                <Rodal visible={showDelete} onClose={handleCloseDelete} className="rodal" customStyles={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'
                }}>
                    <div className="modal-body" style={{ marginTop: "20px", display: "flex", alignItems: "center" }}>{resources["AreYouSureYouWantToDeleteLinkAgenda"]}</div>
                    <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                        <button id="btnCancelDelete" className="btn btn-secondary" style={{ backgroundColor: "white", color: "#53B3EA", borderColor: "#53B3EA" }} onClick={handleCloseDelete}
                            disabled={requestInProgress}>{resources["Cancel"]}</button>
                        <button className="btn btn-primary" id="btnDelete" style={{ backgroundColor: "#53B3EA", color: "white", borderColor: "#53B3EA" }} onClick={handleDeleteAgenda}
                            disabled={requestInProgress}>{resources["Yes"]}</button>
                    </div>
                    <span className="rodal-close"></span>
                </Rodal>

            </div>
        </div >
    );
};

export default withPlanningSettings(LinkAgendasTable);
